<template>
  <div>
    <b-row class="mb-3">
      <b-col md="8" class="mb-3">
        <label class="lbl">Nome Completo</label>
        <b-form-input
          v-model="local.nome"
          class="inp"
          placeholder="Nome completo"
          :state="nomeState(local.nome)"
        />
        <b-form-invalid-feedback :state="nomeState(local.nome)"
          >Informe o nome do contato.</b-form-invalid-feedback
        >
      </b-col>
      <b-col md="4" class="mb-3">
        <label class="lbl">CPF</label>
        <b-form-input
          v-model="local.cpf"
          class="inp"
          inputmode="numeric"
          placeholder="000.000.000-00"
          :state="cpfState(local.cpf)"
        />
        <b-form-invalid-feedback :state="cpfState(local.cpf)">
          {{ !local.cpf ? 'CPF é obrigatório.' : 'CPF inválido.' }}
        </b-form-invalid-feedback>
      </b-col>
    </b-row>

    <!-- E-mails -->
    <div class="mb-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <span class="lbl mb-0">E-mails</span>
        <b-button
          v-if="allowAddEmail"
          variant="primary"
          size="sm"
          @click="addEmail"
        >
          <i class="fa fa-plus mr-1"></i> Adicionar
        </b-button>
      </div>
      <b-row
        v-for="(email, i) in local.emails"
        :key="'em-' + i"
        class="mb-2 align-items-center"
        no-gutters
      >
        <b-col cols="6" class="pr-1">
          <b-form-input
            v-model.trim="email.valor"
            type="email"
            inputmode="email"
            class="inp"
            placeholder="email@dominio.com"
            :state="emailValido(email.valor)"
          />
        </b-col>
        <b-col cols="5" class="px-1">
          <b-form-select
            v-model="email.relacao"
            :options="opcoesRelacao"
            class="inp"
          />
        </b-col>
        <b-col cols="1" class="pl-1 d-flex align-items-center justify-content-center">
          <button
            type="button"
            class="rm-btn"
            :disabled="!podeRemoverEmail(i)"
            :title="!emailValido(email.valor) ? 'Preencha o e-mail para remover' : !podeRemoverEmail(i) ? 'Obrigatório ter ao menos um e-mail válido' : 'Remover'"
            @click="removeEmail(i)"
          >&times;</button>
        </b-col>
      </b-row>
      <p
        v-if="!local.emails || !local.emails.length"
        class="text-muted small mb-0"
      >
        Nenhum e-mail.
      </p>
    </div>

    <!-- Telefones -->
    <div class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <span class="lbl mb-0">Telefones</span>
        <b-button
          variant="success"
          size="sm"
          :disabled="local.telefones.length >= 2"
          :title="local.telefones.length >= 2 ? 'Máximo de 2 telefones por contato' : ''"
          @click="addTelefone"
        >
          <i class="fa fa-plus mr-1"></i> Adicionar
        </b-button>
      </div>
      <b-row
        v-for="(tel, i) in local.telefones"
        :key="'tel-' + i"
        class="mb-2 align-items-center"
        no-gutters
      >
        <b-col cols="5" class="pr-1">
          <b-form-select
            v-model="tel.tipoClassificacao"
            :options="opcoesClassificacao"
            class="inp"
          />
        </b-col>
        <b-col cols="6" class="px-1">
          <b-form-input
            v-model="tel.numero"
            :formatter="formatarApenasNumeros"
            class="inp"
            inputmode="numeric"
            placeholder="Apenas números"
            :state="telefoneValido(tel.numero)"
          />
        </b-col>
        <b-col cols="1" class="pl-1 d-flex align-items-center justify-content-center">
          <button
            type="button"
            class="rm-btn"
            :disabled="!podeRemoverTelefone(i)"
            :title="!telefoneValido(tel.numero) ? 'Preencha o telefone para remover' : !podeRemoverTelefone(i) ? 'Obrigatório ter ao menos um telefone válido' : 'Remover'"
            @click="removeTelefone(i)"
          >&times;</button>
        </b-col>
      </b-row>
      <p
        v-if="!local.telefones || !local.telefones.length"
        class="text-muted small mb-0"
      >
        Nenhum telefone.
      </p>
    </div>

    <!-- Preferências -->
    <b-row class="mb-4">
      <b-col md="6" class="mb-3 mb-md-0">
        <label class="lbl">Forma de Contato Preferencial</label>
        <b-form-select
          v-model="local.preferencia"
          :options="opcoesPreferencia"
          class="inp"
        />
      </b-col>
      <b-col md="6" v-if="isPrincipal">
        <label class="lbl">Tipo de Comunicação</label>
        <p class="tipo-hint">
          <i class="fa fa-info-circle mr-1"></i>Os e-mails cadastrados acima receberão as comunicações marcadas.
        </p>
        <div class="mt-2 d-flex flex-wrap" style="gap: 12px">
          <b-form-checkbox
            v-for="op in opcoesComunicacao"
            :key="op.value"
            v-model="local.tipoComunicacao"
            :value="op.value"
            :disabled="op.value === 'B'"
            >{{ op.text }}</b-form-checkbox
          >
        </div>
        <small class="boleto-lock-hint">
          <i class="fa fa-lock mr-1"></i>Boletos não pode ser desmarcado — obrigatório no contato principal.
        </small>
      </b-col>
    </b-row>

    <!-- Responsável Legal (apenas para contato principal) -->
    <div v-if="showResponsavelLegal" class="mb-4">
      <b-form-checkbox
        :checked="local.responsavelLegal"
        :disabled="local.responsavelLegal"
        @change="onResponsavelLegalChange"
      >
        <span class="font-weight-bold"
          >Este contato é um Responsável Legal pela empresa.</span
        >
      </b-form-checkbox>
    </div>

    <!-- Ações -->
    <div class="d-flex justify-content-between align-items-center">
      <b-button
        v-if="enableDeactivation"
        variant="danger"
        size="sm"
        @click="$emit('deactivate')"
      >
        <i class="fa fa-user-times mr-1"></i> Inativar Contato
      </b-button>
      <div v-else></div>

      <b-button
        variant="success"
        size="sm"
        class="px-4 font-weight-bold"
        :disabled="!!local.carregando || !formularioValido || !dadosModificados"
        @click="salvar"
      >
        <b-spinner small v-if="local.carregando" class="mr-1" />
        <i class="fa fa-check mr-1" v-else></i>
        {{ local.carregando ? "Salvando..." : "Salvar" }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { clearStr, validateCPF, validateEmail } from "../../helpers";

export default {
  name: "ContatoCard",
  props: {
    model: { type: Object, default: () => ({}) },
    requiredAll: { type: Boolean, default: false },
    allowAddEmail: { type: Boolean, default: false },
    enableDeactivation: { type: Boolean, default: false },
    showResponsavelLegal: { type: Boolean, default: false },
    otherContacts: { type: Array, default: () => [] },
    isPrincipal: { type: Boolean, default: false },
  },
  data() {
    const emails = this.model && this.model.emails ? this.model.emails : [];
    const tels = this.model && this.model.telefones ? this.model.telefones : [];
    return {
      local: this.cloneModel(this.model),
      origEmailCount: emails.length,
      origTelCount: tels.length,
      opcoesRelacao: [
        { value: "EC", text: "Corporativo" },
        { value: "EP", text: "Pessoal" },
      ],
      opcoesClassificacao: [
        { value: "P", text: "Telefone Pessoal" },
        { value: "C", text: "Telefone Corporativo" },
      ],
      opcoesPreferencia: [
        { value: "E", text: "E-mail" },
        { value: "W", text: "WhatsApp" },
        { value: "L", text: "Ligação Telefônica" },
        { value: "V", text: "Visita Presencial" },
      ],
      opcoesComunicacao: [
        { value: "B", text: "Boletos" },
        { value: "C", text: "Comunicados e soluções" },
      ],
    };
  },
  watch: {
    model: {
      handler(val) {
        const localStr = JSON.stringify(this.cloneModel(val));
        if (JSON.stringify(this.local) !== localStr) {
          this.local = this.cloneModel(val);
          this.origEmailCount = val && val.emails ? val.emails.length : 0;
          this.origTelCount = val && val.telefones ? val.telefones.length : 0;
        }
      },
      deep: true,
    },
    "local.tipoComunicacao": {
      deep: true,
      handler(val) {
        this.$emit("comunicacao-change", val ? [...val] : []);
      },
    },
    dadosModificados(val) {
      this.$emit("dirty-change", val);
    },
  },
  computed: {
    eUltimoComBoleto() {
      const esteTemBoleto = (this.local.tipoComunicacao || []).includes("B");
      const outroTemBoleto = this.otherContacts.some((tc) =>
        (tc || []).includes("B")
      );
      return esteTemBoleto && !outroTemBoleto;
    },
    algumTemBoleto() {
      const esteTemBoleto = (this.local.tipoComunicacao || []).includes("B");
      const outroTemBoleto = this.otherContacts.some((tc) =>
        (tc || []).includes("B")
      );
      return esteTemBoleto || outroTemBoleto;
    },
    formularioValido() {
      if (!this.nomeState(this.local.nome)) return false;
      if (this.cpfState(this.local.cpf) === false) return false;
      const emails = this.local.emails || [];
      if (!emails.length || emails.some((e) => !this.emailValido(e.valor))) return false;
      const tels = this.local.telefones || [];
      if (!tels.length || tels.some((t) => !this.telefoneValido(t.numero))) return false;
      if (!this.algumTemBoleto) return false;
      return true;
    },
    dadosModificados() {
      const sem = (obj) => {
        const clone = Object.assign({}, obj);
        delete clone.carregando;
        return clone;
      };
      return (
        JSON.stringify(sem(this.cloneModel(this.model))) !==
        JSON.stringify(sem(this.local))
      );
    },
  },
  mounted() {
    if (this.isPrincipal && !(this.local.tipoComunicacao || []).includes('B')) {
      this.local.tipoComunicacao = [...(this.local.tipoComunicacao || []), 'B'];
    }
    this.$emit("comunicacao-change", [...(this.local.tipoComunicacao || [])]);
  },
  methods: {
    cloneModel(m) {
      if (!m)
        return {
          nome: "",
          cpf: "",
          preferencia: "E",
          tipoComunicacao: [],
          responsavelLegal: false,
          emails: [],
          telefones: [],
          carregando: false,
        };
      return {
        ...m,
        preferencia: m.preferencia || "E",
        tipoComunicacao: Array.isArray(m.tipoComunicacao)
          ? [...m.tipoComunicacao]
          : [],
        responsavelLegal: !!m.responsavelLegal,
        emails: (m.emails || []).map((e) => ({ ...e })),
        telefones: (m.telefones || []).map((t) => ({
          ...t,
          tipoClassificacao: this.normalizarClassificacaoTelefone(
            t.tipoClassificacao
          ),
        })),
      };
    },
    normalizarClassificacaoTelefone(value) {
      const v = String(value || "").toUpperCase().trim();
      // Normaliza legados de 2 letras → P ou C
      if (["TC", "CC"].includes(v)) return "C";
      if (v === "C") return "C";
      return "P";
    },
    relacaoTelefoneFromClassificacao(value) {
      return this.normalizarClassificacaoTelefone(value);
    },
    tipoTelefoneFromClassificacao() {
      return "T";
    },
    podeRemoverEmail(i) {
      const isNovo = i >= this.origEmailCount;
      if (isNovo) return true; // linha nova: X sempre habilitado para cancelar adição
      // linha do banco: só remove se sobrar ao menos 1 e-mail válido
      return this.local.emails.filter((e, idx) => idx !== i && this.emailValido(e.valor)).length >= 1;
    },
    podeRemoverTelefone(i) {
      const isNovo = i >= this.origTelCount;
      if (isNovo) return true; // linha nova: X sempre habilitado para cancelar adição
      // linha do banco: só remove se sobrar ao menos 1 telefone válido
      return this.local.telefones.filter((t, idx) => idx !== i && this.telefoneValido(t.numero)).length >= 1;
    },
    emailValido(value) {
      const email = String(value || "").trim();
      return !!email && validateEmail(email);
    },
    formatarApenasNumeros(value) {
      return String(value || "").replace(/\D/g, "");
    },
    telefoneValido(value) {
      const numero = String(value || "").trim();
      return !!numero && /^\d+$/.test(numero);
    },
    nomeState(value) {
      const nome = String(value || "").trim();
      if (!nome) return false;
      return nome.length >= 3;
    },
    cpfValido(value) {
      const cpf = clearStr(String(value || ""));
      return !!cpf && validateCPF(cpf);
    },
    cpfState(value) {
      const cpf = clearStr(String(value || ""));
      if (!cpf) return false;
      return validateCPF(cpf);
    },
    onResponsavelLegalChange(val) {
      if (val && !this.local.responsavelLegal) {
        this.$emit("definir-responsavel-legal");
      }
    },
    addEmail() {
      this.local.emails.push({ valor: "", relacao: "EC" });
    },
    removeEmail(i) {
      if (i < this.origEmailCount) this.origEmailCount--;
      this.local.emails.splice(i, 1);
    },
    addTelefone() {
      if (this.local.telefones.length >= 2) return;
      this.local.telefones.push({ numero: "", tipoClassificacao: "C" });
    },
    removeTelefone(i) {
      if (i < this.origTelCount) this.origTelCount--;
      this.local.telefones.splice(i, 1);
    },
    salvar() {
      if (!this.local.cpf || !this.cpfValido(this.local.cpf)) {
        alert("CPF é obrigatório e deve ser válido.");
        return;
      }
      const emailsValidos = (this.local.emails || []).filter(
        (e) => e.valor && e.valor.trim()
      );
      if (emailsValidos.some((e) => !this.emailValido(e.valor))) {
        alert("Informe e-mails válidos.");
        return;
      }
      if (emailsValidos.length === 0) {
        alert("O contato deve ter pelo menos um e-mail.");
        return;
      }
      const telefonesValidos = (this.local.telefones || []).filter(
        (t) => t.numero && t.numero.trim()
      );
      if (telefonesValidos.some((t) => !this.telefoneValido(t.numero))) {
        alert("Informe telefones válidos apenas com números.");
        return;
      }
      if (telefonesValidos.length === 0) {
        alert("O contato deve ter pelo menos um telefone.");
        return;
      }
      const payload = {
        ...this.local,
        cpf: this.local.cpf ? String(this.local.cpf).replace(/\D/g, "") : "",
        emails: (this.local.emails || []).map((email) => ({
          ...email,
          valor: String(email.valor || "").trim(),
        })),
        telefones: (this.local.telefones || []).map((telefone) => ({
          ...telefone,
          numero: telefone.numero
            ? String(telefone.numero).replace(/\D/g, "")
            : "",
          tipoClassificacao: this.normalizarClassificacaoTelefone(
            telefone.tipoClassificacao
          ),
          relacao: this.relacaoTelefoneFromClassificacao(
            telefone.tipoClassificacao
          ),
          tipoTelefone: this.tipoTelefoneFromClassificacao(
            telefone.tipoClassificacao
          ),
        })),
      };
      console.log('[DEBUG ContatoCard.salvar] emails:', JSON.stringify(payload.emails.map(e => ({ valor: e.valor, relacao: e.relacao }))));
      this.$emit("update:model", { ...payload });
      this.$emit("save", payload);
    },
  },
};
</script>

<style scoped>
.lbl {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #64748b;
  margin-bottom: 6px;
}
.tipo-hint {
  font-size: 0.78rem;
  color: #16B3D3;
  margin-bottom: 6px;
  margin-top: 2px;
}
.boleto-lock-hint {
  display: block;
  margin-top: 6px;
  font-size: 0.75rem;
  color: #856404;
}
.boleto-aviso {
  display: block;
  margin-top: 6px;
  font-size: 0.75rem;
  color: #dc3545;
  font-weight: 600;
}
.inp {
  height: 40px !important;
  padding: 0 14px !important;
  border-radius: 10px !important;
  border: 1.5px solid #e2e8f0 !important;
  font-size: 0.875rem !important;
  background: #ffffff !important;
  color: #0f172a !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.inp:focus {
  border-color: #05428E !important;
  box-shadow: 0 0 0 4px rgba(5, 66, 142, 0.12) !important;
  outline: none !important;
}
.inp:hover:not(:focus):not(.is-invalid):not(.is-valid) {
  border-color: #cbd5e1 !important;
}
.inp.is-valid {
  border-color: #00BD80 !important;
  background: rgba(0, 189, 128, 0.03) !important;
  background-image: none !important;
  padding-right: 14px !important;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04) !important;
}
.inp.is-valid:focus {
  box-shadow: 0 0 0 4px rgba(0, 189, 128, 0.12) !important;
}
.inp.is-invalid {
  border-color: #D42859 !important;
  background: rgba(212, 40, 89, 0.03) !important;
  background-image: none !important;
  padding-right: 14px !important;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04) !important;
}
.inp.is-invalid:focus {
  box-shadow: 0 0 0 4px rgba(212, 40, 89, 0.12) !important;
}

.rm-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1.5px solid #D42859;
  background: #D42859;
  color: #fff;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: all 0.18s;
  user-select: none;
}
.rm-btn--disabled,
.rm-btn:disabled {
  background: #ccc !important;
  border-color: #ccc !important;
  color: #fff !important;
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}
.rm-btn:hover:not(:disabled),
.rm-btn:focus:not(:disabled) {
  background: #a81e46;
  border-color: #a81e46;
  outline: none;
}
.rm-ph {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}
</style>
